import { useState, useMemo, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { useCanCreateParty } from '@/hooks/useCanCreateParty'
import { useCreateParty } from '@/hooks/useCreateParty'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { usePartyFormValidation } from '@/hooks/useValidation'
import { createNeutralVector } from '@/lib/ideology'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { IdeologyVector } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ColorPicker } from '@/components/ui/ColorPicker'
import { IdeologyEditor } from '@/components/ideology/IdeologyEditor'
import { cn } from '@/lib/utils'

export default function PartyCreate() {
  const { nationId } = useParams<{ nationId: string }>()
  const navigate = useNavigate()
  const { canCreate, isLoading: isCheckingPermission } = useCanCreateParty(nationId)
  const { createPartyAsync, isCreating } = useCreateParty()
  const { nation } = useNation(nationId)
  const { world } = useWorld(nation?.world_id)

  // Form fields
  const [name, setName] = useState('')
  const [abbreviation, setAbbreviation] = useState('')
  const [color, setColor] = useState('#1E88E5')
  const [slogan, setSlogan] = useState('')
  const [ideologyVector, setIdeologyVector] = useState<IdeologyVector>(createNeutralVector())

  // Validation
  const {
    validate,
    clearFieldError,
    getFieldError,
  } = usePartyFormValidation()

  // Form state
  const [error, setError] = useState<string | null>(null)

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name, href: `/nations/${nation.id}` })
    }
    items.push({ label: 'Create Party' })
    return items
  }, [world, nation])

  const handleAbbreviationChange = (value: string) => {
    // Auto-uppercase and limit to 6 chars
    setAbbreviation(value.toUpperCase().slice(0, 6))
    if (getFieldError('abbreviation')) {
      clearFieldError('abbreviation')
    }
  }

  // Handle field changes with validation clearing
  const handleNameChange = (value: string) => {
    setName(value)
    if (getFieldError('name')) {
      clearFieldError('name')
    }
  }

  const handleColorChange = (value: string) => {
    setColor(value)
    if (getFieldError('color')) {
      clearFieldError('color')
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate using the validation hook
    const result = validate({
      name,
      abbreviation,
      color,
      slogan,
      ideology_vector: ideologyVector,
    })

    if (!result.valid) {
      // Show first error as the general error message
      const firstError = Object.values(result.fieldErrors)[0]
      if (firstError) {
        setError(firstError)
      }
      return
    }

    if (!nationId) {
      setError('Nation ID is missing')
      return
    }

    try {
      const party = await createPartyAsync({
        nation_id: nationId,
        name: name.trim(),
        abbreviation: abbreviation.trim(),
        color: color,
        slogan: slogan.trim() || undefined,
        ideology_vector: ideologyVector,
      })
      navigate(`/parties/${party.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create party')
    }
  }

  const handleCancel = () => {
    navigate(`/nations/${nationId}`)
  }

  // Show loading while checking permission
  if (isCheckingPermission) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </AppLayout>
    )
  }

  // Deny access if user cannot create party
  if (!canCreate?.allowed) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle>Cannot Create Party</CardTitle>
            <CardDescription>
              {canCreate?.reason || 'You are not allowed to create a party in this nation.'}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="outline" onClick={() => navigate(`/nations/${nationId}`)}>
              Back to Nation
            </Button>
          </CardFooter>
        </Card>
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <div className="mx-auto max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle>Create Your Party</CardTitle>
            <CardDescription>
              {nation ? (
                <>Found your political party in <strong>{nation.name}</strong>. Define your identity and ideology.</>
              ) : (
                'Define your party identity and ideology.'
              )}
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-8">
              {error && (
                <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                  {error}
                </div>
              )}

              {/* Party Identity Section */}
              <section className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Party Identity</h3>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="e.g., Progressive Alliance"
                      value={name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      maxLength={50}
                      required
                      disabled={isCreating}
                      className={cn(getFieldError('name') && 'border-destructive')}
                    />
                    <p className={cn(
                      'text-xs',
                      getFieldError('name') ? 'text-destructive' : 'text-muted-foreground'
                    )}>
                      {getFieldError('name') || `${name.length}/50 characters`}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="abbreviation">Abbreviation *</Label>
                    <Input
                      id="abbreviation"
                      type="text"
                      placeholder="e.g., PA"
                      value={abbreviation}
                      onChange={(e) => handleAbbreviationChange(e.target.value)}
                      maxLength={6}
                      required
                      disabled={isCreating}
                      className={cn('uppercase', getFieldError('abbreviation') && 'border-destructive')}
                    />
                    <p className={cn(
                      'text-xs',
                      getFieldError('abbreviation') ? 'text-destructive' : 'text-muted-foreground'
                    )}>
                      {getFieldError('abbreviation') || '2-6 uppercase letters'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Party Color *</Label>
                  <ColorPicker
                    value={color}
                    onChange={handleColorChange}
                  />
                  {getFieldError('color') && (
                    <p className="text-xs text-destructive">{getFieldError('color')}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slogan">Slogan (optional)</Label>
                  <Input
                    id="slogan"
                    type="text"
                    placeholder="e.g., Building tomorrow, together"
                    value={slogan}
                    onChange={(e) => setSlogan(e.target.value)}
                    maxLength={100}
                    disabled={isCreating}
                  />
                  <p className="text-xs text-muted-foreground">
                    {slogan.length}/100 characters
                  </p>
                </div>
              </section>

              {/* Ideology Position Section */}
              <section className="space-y-4">
                <div className="border-b pb-2">
                  <h3 className="text-lg font-semibold">Ideology Position</h3>
                  <p className="text-sm text-muted-foreground">
                    Position your party on 7 political axes. This determines which voters you appeal to.
                  </p>
                </div>

                <IdeologyEditor
                  value={ideologyVector}
                  onChange={setIdeologyVector}
                  partyColor={color}
                  disabled={isCreating}
                  showPreview
                />
              </section>
            </CardContent>

            <CardFooter className="flex gap-3 border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? 'Creating Party...' : 'Create Party'}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </AppLayout>
  )
}
